/**
 * Sentence Editing Stage (Step 1.5 — text workflow)
 *
 * Shows AI-segmented sentences for creator review and editing before
 * the full enrichment parsing (translations, grammar points, etc.) runs.
 *
 * Operations per sentence: edit text, edit speaker, split, merge-next, delete
 * Global: add new sentence, confirm and proceed to parsing
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseDraft } from '../../../types/creator-workflow';
import './SentenceEditingStage.css';

interface SentenceDraft {
  index: number;
  text: string;
  speaker?: string | null;
}

interface SplitState {
  firstText: string;
  secondText: string;
  secondSpeaker: string;
}

interface SentenceEditingStageProps {
  draft: CourseDraft;
  loading: boolean;
  onUpdateSentenceEditing: (index: number, data: { text: string; speaker?: string | null }) => Promise<void>;
  onSplitSentenceEditing: (index: number, data: { firstText: string; secondText: string; secondSpeaker?: string | null }) => Promise<void>;
  onMergeNextSentenceEditing: (index: number) => Promise<void>;
  onDeleteSentenceEditing: (index: number) => Promise<void>;
  onAddSentenceEditing: (data: { afterIndex?: number | null; text: string; speaker?: string | null }) => Promise<void>;
  onConfirmAndParse: () => Promise<void>;
  onGoBackStage: () => Promise<void>;
}

const SentenceEditingStage: React.FC<SentenceEditingStageProps> = ({
  draft,
  loading,
  onUpdateSentenceEditing,
  onSplitSentenceEditing,
  onMergeNextSentenceEditing,
  onDeleteSentenceEditing,
  onAddSentenceEditing,
  onConfirmAndParse,
  onGoBackStage,
}) => {
  const { t } = useTranslation('creator');

  const sentences: SentenceDraft[] = (draft.sentences || []).map((s: any, idx: number) => ({
    index: idx,
    text: s.text || '',
    speaker: s.speaker || null,
  }));

  // Per-sentence edit state
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editSpeaker, setEditSpeaker] = useState('');

  // Split UI state
  const [splittingIndex, setSplittingIndex] = useState<number | null>(null);
  const [splitState, setSplitState] = useState<SplitState>({ firstText: '', secondText: '', secondSpeaker: '' });

  // New sentence state
  const [addingAfterIndex, setAddingAfterIndex] = useState<number | null>(null);
  const [newText, setNewText] = useState('');
  const [newSpeaker, setNewSpeaker] = useState('');

  const [actionError, setActionError] = useState<string | null>(null);

  const handleStartEdit = (sentence: SentenceDraft) => {
    setEditingIndex(sentence.index);
    setEditText(sentence.text);
    setEditSpeaker(sentence.speaker || '');
    setSplittingIndex(null);
    setAddingAfterIndex(null);
  };

  const handleSaveEdit = useCallback(async () => {
    if (editingIndex === null) return;
    setActionError(null);
    try {
      await onUpdateSentenceEditing(editingIndex, {
        text: editText.trim(),
        speaker: editSpeaker.trim() || null,
      });
      setEditingIndex(null);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Update failed');
    }
  }, [editingIndex, editText, editSpeaker, onUpdateSentenceEditing]);

  const handleCancelEdit = () => setEditingIndex(null);

  const handleStartSplit = (sentence: SentenceDraft) => {
    // Pre-fill with the full text in firstText, empty secondText
    setSplittingIndex(sentence.index);
    setSplitState({ firstText: sentence.text, secondText: '', secondSpeaker: sentence.speaker || '' });
    setEditingIndex(null);
    setAddingAfterIndex(null);
  };

  const handleDoSplit = useCallback(async () => {
    if (splittingIndex === null) return;
    if (!splitState.firstText.trim() || !splitState.secondText.trim()) {
      setActionError('Both parts must have text.');
      return;
    }
    setActionError(null);
    try {
      await onSplitSentenceEditing(splittingIndex, {
        firstText: splitState.firstText.trim(),
        secondText: splitState.secondText.trim(),
        secondSpeaker: splitState.secondSpeaker.trim() || null,
      });
      setSplittingIndex(null);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Split failed');
    }
  }, [splittingIndex, splitState, onSplitSentenceEditing]);

  const handleMergeNext = useCallback(async (index: number) => {
    setActionError(null);
    try {
      await onMergeNextSentenceEditing(index);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Merge failed');
    }
  }, [onMergeNextSentenceEditing]);

  const handleDelete = useCallback(async (index: number) => {
    if (!window.confirm(t('workflow.sentenceEditing.confirmDelete'))) return;
    setActionError(null);
    try {
      await onDeleteSentenceEditing(index);
      if (editingIndex === index) setEditingIndex(null);
      if (splittingIndex === index) setSplittingIndex(null);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Delete failed');
    }
  }, [editingIndex, splittingIndex, onDeleteSentenceEditing, t]);

  const handleStartAdd = (afterIndex: number | null) => {
    setAddingAfterIndex(afterIndex);
    setNewText('');
    setNewSpeaker('');
    setEditingIndex(null);
    setSplittingIndex(null);
    setActionError(null);
  };

  const handleDoAdd = useCallback(async () => {
    if (!newText.trim()) {
      setActionError('Sentence text cannot be empty.');
      return;
    }
    setActionError(null);
    try {
      await onAddSentenceEditing({
        afterIndex: addingAfterIndex,
        text: newText.trim(),
        speaker: newSpeaker.trim() || null,
      });
      setAddingAfterIndex(null);
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Add failed');
    }
  }, [addingAfterIndex, newText, newSpeaker, onAddSentenceEditing]);

  const handleConfirm = useCallback(async () => {
    setActionError(null);
    try {
      await onConfirmAndParse();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || err?.message || 'Failed to start analysis');
    }
  }, [onConfirmAndParse]);

  return (
    <div className="sentence-editing-stage">
      <header className="stage-header">
        <h2>{t('workflow.sentenceEditing.title')}</h2>
        <p className="stage-description">
          {t('workflow.sentenceEditing.description')}
        </p>
      </header>

      <div className="sentence-count-info">
        <span>{t('workflow.sentenceEditing.sentenceCount', { count: sentences.length })}</span>
      </div>

      {actionError && (
        <div className="action-error">
          ⚠️ {actionError}
        </div>
      )}

      <div className="sentences-list">
        {sentences.map((sentence, listIdx) => (
          <div key={listIdx} className="sentence-item">
            {/* Normal display */}
            {editingIndex !== sentence.index && splittingIndex !== sentence.index && (
              <div className="sentence-display">
                <div className="sentence-meta">
                  <span className="sentence-number">#{sentence.index + 1}</span>
                  {sentence.speaker && (
                    <span className="sentence-speaker">{sentence.speaker}</span>
                  )}
                </div>
                <div className="sentence-text">{sentence.text}</div>
                <div className="sentence-actions">
                  <button
                    className="btn-action btn-edit"
                    onClick={() => handleStartEdit(sentence)}
                    disabled={loading}
                    title={t('workflow.sentenceEditing.editTitle')}
                  >
                    {t('workflow.sentenceEditing.editBtn')}
                  </button>
                  <button
                    className="btn-action btn-split"
                    onClick={() => handleStartSplit(sentence)}
                    disabled={loading}
                    title={t('workflow.sentenceEditing.splitTitle')}
                  >
                    {t('workflow.sentenceEditing.splitBtn')}
                  </button>
                  {sentence.index < sentences.length - 1 && (
                    <button
                      className="btn-action btn-merge"
                      onClick={() => handleMergeNext(sentence.index)}
                      disabled={loading}
                      title={t('workflow.sentenceEditing.mergeTitle')}
                    >
                      {t('workflow.sentenceEditing.mergeBtn')}
                    </button>
                  )}
                  <button
                    className="btn-action btn-delete"
                    onClick={() => handleDelete(sentence.index)}
                    disabled={loading || sentences.length <= 1}
                    title={t('workflow.sentenceEditing.deleteTitle')}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            )}

            {/* Inline edit mode */}
            {editingIndex === sentence.index && (
              <div className="sentence-edit-form">
                <div className="form-group">
                  <label>{t('workflow.sentenceEditing.speakerLabel')}</label>
                  <input
                    type="text"
                    value={editSpeaker}
                    onChange={e => setEditSpeaker(e.target.value)}
                    placeholder={t('workflow.sentenceEditing.speakerPlaceholderA')}
                    className="input-speaker"
                  />
                </div>
                <div className="form-group">
                  <label>{t('workflow.sentenceEditing.sentenceTextLabel')}</label>
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    rows={3}
                    className="input-text"
                  />
                </div>
                <div className="form-row-actions">
                  <button className="btn-primary btn-sm" onClick={handleSaveEdit} disabled={loading}>
                    {t('workflow.sentenceEditing.save')}
                  </button>
                  <button className="btn-secondary btn-sm" onClick={handleCancelEdit} disabled={loading}>
                    {t('workflow.sentenceEditing.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Inline split mode */}
            {splittingIndex === sentence.index && (
              <div className="sentence-split-form">
                <p className="split-instructions">
                  {t('workflow.sentenceEditing.splitInstructions')}
                </p>
                <div className="form-group">
                  <label>{t('workflow.sentenceEditing.firstSentence')}</label>
                  <textarea
                    value={splitState.firstText}
                    onChange={e => setSplitState(s => ({ ...s, firstText: e.target.value }))}
                    rows={2}
                    className="input-text"
                  />
                </div>
                <div className="form-group">
                  <label>{t('workflow.sentenceEditing.secondSentence')}</label>
                  <textarea
                    value={splitState.secondText}
                    onChange={e => setSplitState(s => ({ ...s, secondText: e.target.value }))}
                    rows={2}
                    className="input-text"
                    placeholder={t('workflow.sentenceEditing.secondSentencePlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('workflow.sentenceEditing.speakerForSecond')}</label>
                  <input
                    type="text"
                    value={splitState.secondSpeaker}
                    onChange={e => setSplitState(s => ({ ...s, secondSpeaker: e.target.value }))}
                    placeholder={t('workflow.sentenceEditing.speakerForSecondPlaceholder')}
                    className="input-speaker"
                  />
                </div>
                <div className="form-row-actions">
                  <button className="btn-primary btn-sm" onClick={handleDoSplit} disabled={loading}>
                    {t('workflow.sentenceEditing.confirmSplit')}
                  </button>
                  <button className="btn-secondary btn-sm" onClick={() => setSplittingIndex(null)} disabled={loading}>
                    {t('workflow.sentenceEditing.cancel')}
                  </button>
                </div>
              </div>
            )}

            {/* Add sentence after this one */}
            <div className="add-after-row">
              {addingAfterIndex === sentence.index ? (
                <div className="sentence-add-form">
                  <div className="form-group">
                    <label>{t('workflow.sentenceEditing.speakerLabel')}</label>
                    <input
                      type="text"
                      value={newSpeaker}
                      onChange={e => setNewSpeaker(e.target.value)}
                      placeholder={t('workflow.sentenceEditing.speakerPlaceholderB')}
                      className="input-speaker"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('workflow.sentenceEditing.newSentenceLabel')}</label>
                    <textarea
                      value={newText}
                      onChange={e => setNewText(e.target.value)}
                      rows={2}
                      className="input-text"
                      placeholder={t('workflow.sentenceEditing.newSentencePlaceholder')}
                    />
                  </div>
                  <div className="form-row-actions">
                    <button className="btn-primary btn-sm" onClick={handleDoAdd} disabled={loading}>
                      {t('workflow.sentenceEditing.add')}
                    </button>
                    <button className="btn-secondary btn-sm" onClick={() => setAddingAfterIndex(null)} disabled={loading}>
                      {t('workflow.sentenceEditing.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-add-after"
                  onClick={() => handleStartAdd(sentence.index)}
                  disabled={loading}
                  title={t('workflow.sentenceEditing.addAfterTitle')}
                >
                  {t('workflow.sentenceEditing.addAfterBtn')}
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Add at end button (when not already adding at end) */}
        {addingAfterIndex === null && (
          <div className="add-at-end">
            <button
              className="btn-add-end"
              onClick={() => handleStartAdd(null)}
              disabled={loading}
            >
              {t('workflow.sentenceEditing.addAtEndBtn')}
            </button>
          </div>
        )}

      </div>

      <div className="stage-footer">
        <button
          className="btn-secondary"
          onClick={onGoBackStage}
          disabled={loading}
        >
          {t('workflow.sentenceEditing.backToGeneration')}
        </button>
        <button
          className="btn-primary btn-confirm"
          onClick={handleConfirm}
          disabled={loading || sentences.length === 0}
        >
          {loading
            ? t('workflow.sentenceEditing.analyzing')
            : t('workflow.sentenceEditing.confirmAndAnalyze', { count: sentences.length })}
        </button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner" />
          <p>{t('workflow.sentenceEditing.analyzingContent')}</p>
          <span className="loading-hint">{t('workflow.sentenceEditing.analyzingHint')}</span>
        </div>
      )}
    </div>
  );
};

export default SentenceEditingStage;
